import { NavLink, useLocation } from "react-router-dom";
import { getActiveAccount, getMsalInstance, getScopes, RETURN_TO_KEY } from "../auth/msalConfig.js";
import { canSwitchAccount } from "../api/accountSwitch.js";
import { useMe, usePortalNavEntries } from "../api/portalQueries.js";
import type { TenantBrand } from "../theme/tenantBrand.js";
import { TenantSwitcher, useTenantQuery } from "./TenantSelection.js";

/**
 * Phase 342 wave 4 task 4.1 — the client portal's navigation rail.
 *
 * <h3>What it can show</h3>
 * <p>Exactly the reports the signed-in client's tenant has configured, and nothing else.
 * The items are the intersection of the families the engine reports against the families
 * this app has pages for, so configuring a second report for a tenant makes it appear here
 * with no portal deploy, and a family with no page cannot produce a dead link.</p>
 *
 * <h3>What it cannot show</h3>
 * <p>There is no operator surface here, and no tenant selector for a CLIENT — in ANY state,
 * including loading and error. (Walk 3: a principal with more than one allowed tenant, i.e. an
 * operator or platform admin, gets a compact switcher under the brand; `TenantSwitcher` renders
 * nothing for anyone else.) That is not a rule this component enforces by omission: per ADR-081 D3
 * the operator screens are not in this bundle at all, so there is nothing for a nav entry to
 * point at. The design-system `NavRail` is deliberately NOT reused for the same reason —
 * its item list and `active` union are hard-coded to the operator console's surfaces, so
 * importing it would pull those names into a client's bundle to then hide them.</p>
 *
 * <h3>Why the rail persists with zero reports</h3>
 * <p>The item list disappears when there is nothing to link to (the landing route shows the
 * empty state instead), but the identity footer stays: a client who signed in as the wrong
 * person needs to see who they are and be able to sign out, and that is most true precisely
 * when the app is showing them nothing.</p>
 *
 * <p>#2513: an AR Dept. person (see api/accountSwitch.ts) also gets "Switch account" there — a
 * Microsoft account picker that returns them to the same page as the chosen account, with no
 * sign-out. Client staff never see it, nor does anyone while `/me` is loading or has failed.</p>
 *
 * <h3>Whose brand it wears</h3>
 * <p>#2663: for a single-tenant client whose firm has a theme (`brand`, from
 * theme/tenantBrand.ts), the brand block is the firm's — its logo alone, named by its alt text,
 * or its name as text when there is no usable logo — and "Powered by The AR Dept." sits at the
 * foot of the rail, above the identity footer. Everyone else (`brand` null) sees exactly the AR
 * Dept. mark and "Reports", and no Powered-by line.</p>
 *
 * <p>Until `/me` answers (`brandPending`) nobody's brand is known, so the brand block is empty —
 * no AR Dept. mark, no logo, no Powered-by — and keeps its row, so nothing moves when the answer
 * lands. A client whose firm has a brand never sees the AR Dept. mark first. A failed `/me` is not
 * pending: it shows the AR Dept. mark.</p>
 */
export function PortalNav({ brand, brandPending }: { brand: TenantBrand | null; brandPending: boolean }) {
  const { entries, email } = usePortalNavEntries();
  // Walk 3: "" for a client, so their links are exactly the bare routes they always were.
  const tenantQuery = useTenantQuery();
  // #2508: `/onboarding` and `/boards/<id>` can both prefix an address (a file page, a board's
  // Import page), so the rail marks the LONGEST matching route only — one highlighted entry, never two.
  const location = useLocation();
  const { pathname } = location;
  const showSwitchAccount = canSwitchAccount(useMe().data);
  const active = activeRoute(entries.map((entry) => entry.route), pathname);

  return (
    <aside className="portal__nav">
      {brandPending ? (
        <div className="portal__brand portal__brand--pending" />
      ) : brand ? (
        <div className="portal__brand">
          {brand.logoUrl !== null ? (
            <img className="portal__logo" src={brand.logoUrl} alt={brand.name} />
          ) : (
            <span className="portal__name">{brand.name}</span>
          )}
        </div>
      ) : (
        <div className="portal__brand">
          <span className="portal__mark" aria-hidden="true">
            A
          </span>
          <span className="portal__name">Reports</span>
        </div>
      )}

      {/* Operators and platform admins only (more than one allowed tenant). A client principal
          renders nothing here, in any state — see TenantSelection.tsx. */}
      <TenantSwitcher />

      {entries.length > 0 && (
        <nav aria-label="Reports">
          <ul className="portal__items">
            {entries.map((entry) => (
              <li key={entry.route}>
                <NavLink
                  to={tenantQuery ? { pathname: entry.route, search: tenantQuery } : entry.route}
                  className={() => `portal__item${entry.route === active ? " is-active" : ""}`}
                >
                  {entry.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="portal__spacer" />

      {brand && <p className="portal__powered">Powered by The AR Dept.</p>}

      <div className="portal__who">
        {email !== null && <b>{email}</b>}
        {showSwitchAccount && (
          <button
            type="button"
            className="portal__switch"
            onClick={() => switchAccount(location.pathname + location.search)}
          >
            Switch account
          </button>
        )}
        <button type="button" className="portal__signout" onClick={signOut}>
          Sign out
        </button>
      </div>
    </aside>
  );
}

/** The nav route the current address is under: the longest one that is a path prefix of it. */
export function activeRoute(routes: readonly string[], pathname: string): string | null {
  let best: string | null = null;
  for (const route of routes) {
    const under = pathname === route || pathname.startsWith(`${route}/`);
    if (under && (best === null || route.length > best.length)) best = route;
  }
  return best;
}

/**
 * #2513 — sign in as another Microsoft account WITHOUT signing out.
 *
 * <p>The current page is stashed exactly as AuthGate stashes a deep link, then MSAL shows the
 * account picker (`prompt: "select_account"`). The round trip comes back through `/auth/callback`,
 * where App's boot pins the chosen account (`setActiveAccount(result.account)`) and AuthCallback
 * returns to the stash. Nothing is logged out and the MSAL cache is left alone, so switching back
 * is the same two clicks.</p>
 */
function switchAccount(returnTo: string): void {
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  getMsalInstance()
    .loginRedirect({ scopes: getScopes(), prompt: "select_account" })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("client-portal: switch-account loginRedirect failed", err);
    });
}

/**
 * End the session at the identity provider, not just in this tab.
 *
 * <p>`logoutRedirect` clears MSAL's local cache AND sends the browser to Entra's logout
 * endpoint, so the next sign-in is a real sign-in. Dropping the local account alone would
 * leave a shared machine one click away from resuming someone else's session.</p>
 */
function signOut(): void {
  void getMsalInstance().logoutRedirect({ account: getActiveAccount() });
}
